import { Link } from "react-router-dom";
import { ShieldCheck, X, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { SITE } from "@/lib/site";

const NOT_WHAT_WE_ARE = [
  "Maritime Thermal Solutions is not a government agency.",
  "We are not a utility company.",
  "We do not complete HVAC installations directly.",
  "Homeowners are under no obligation to purchase anything.",
];

const COMMITMENTS = [
  "Respectful communication at every step.",
  "Transparency about who we are and what we do.",
  "Clear, honest answers — no high-pressure sales.",
  "Representative accountability and proper identification.",
  "Concerns reviewed seriously and addressed in writing.",
];

export default function About() {
  return (
    <>
      <Seo
        title="About Maritime Thermal Solutions | Nova Scotia Homeowner Outreach"
        description="Learn about Maritime Thermal Solutions, a Halifax-based company helping homeowners across Nova Scotia explore residential heat pump options."
      />

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-x py-14 md:py-20">
          <p className="eyebrow">About Us</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold leading-tight text-brand-navy sm:text-5xl" data-testid="about-heading">
            About Maritime Thermal Solutions
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
            We're a Nova Scotia–based company helping homeowners better understand
            residential heating and cooling options. Our work centres on outreach,
            education, and appointment coordination for homeowners interested in
            learning whether a heat pump may be suitable for their home.
          </p>
        </div>
      </section>

      <section className="section-y" data-testid="about-role">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Our Role</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              We help homeowners explore options, not sell equipment.
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
              <p>
                We help homeowners understand their options, answer basic questions, and coordinate
                appointments with partner HVAC companies that serve their area. Appointments are
                completed by partner HVAC companies — they handle the in-home assessment, any quote,
                and the installation work itself.
              </p>
              <p>
                Our team is local and focused on residential heat pump education for Nova Scotia homes.
                We use plain language and avoid pressure tactics, because homeowners deserve clear,
                honest information when they're making a decision about their home.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="font-heading text-lg font-semibold text-slate-900">What we are not</h3>
              <ul className="mt-4 space-y-3" data-testid="about-not-list">
                {NOT_WHAT_WE_ARE.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <X size={18} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="section-y bg-slate-50" data-testid="about-commitment">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Our Commitment</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Built on trust, transparency, and accountability.
            </h2>
            <p className="mt-4 text-slate-600">
              We know that someone arriving at your door asking about heating shouldn't feel
              uncertain or pressured. Our commitments below are how we expect every visit and every
              conversation to go.
            </p>
          </div>
          <ul className="lg:col-span-7 grid gap-3 sm:grid-cols-2" data-testid="about-commitments-list">
            {COMMITMENTS.map((c) => (
              <li key={c} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                <Check size={18} className="mt-0.5 shrink-0 text-brand-navy" aria-hidden="true" />
                <span className="text-sm text-slate-700">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-y" data-testid="about-cta">
        <div className="container-x rounded-lg border border-slate-200 bg-white p-8 sm:p-12">
          <div className="grid items-center gap-6 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 sm:text-3xl">
                Had a representative visit? You can verify them.
              </h2>
              <p className="mt-3 text-slate-600">
                If someone has come to your home representing Maritime Thermal Solutions and you'd
                like to confirm, we'll help you check. You can also reach us by phone at {SITE.phone}.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/verify" className="btn-primary" data-testid="about-verify-cta">
                <ShieldCheck size={18} aria-hidden="true" />
                Verify a Representative
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="about-contact-cta">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
